import { type Infer, v } from "convex/values";
import type { BrandImageSlot } from "@convex/tables/brandKits";

/** A kit image as a page shows it: where it is served, and the file it came from. */
export const brandImageValidator = v.object({ url: v.string(), fileName: v.string() });
export type BrandImage = Infer<typeof brandImageValidator>;

const slotPageImageValidator = v.union(brandImageValidator, v.null());
/** A kit's images as a page shows them; null where the kit leaves the slot empty. */
export const brandImagesValidator = v.object({
    disputewellLogo: slotPageImageValidator,
    disputewellLogoDark: slotPageImageValidator,
    disputewellIcon: slotPageImageValidator,
    fluidMark: slotPageImageValidator,
    fluidMarkDark: slotPageImageValidator,
} satisfies Record<BrandImageSlot, typeof slotPageImageValidator>);
export type BrandImages = Infer<typeof brandImagesValidator>;

/** The Default kit's images: every slot empty, so every surface shows the platform's own image. */
export const NO_BRAND_IMAGES: BrandImages = { disputewellLogo: null, disputewellLogoDark: null, disputewellIcon: null, fluidMark: null, fluidMarkDark: null };

/** The kit a page wears: what the root layout reads for the kit its viewer picked. */
export const wornBrandKitValidator = v.object({ kitId: v.id("brandKits"), name: v.string(), images: brandImagesValidator });
export type WornBrandKit = Infer<typeof wornBrandKitValidator>;

/** A kit as the setting lists it, with whether this member may change it. */
export const brandKitListingValidator = v.object({
    kitId: v.id("brandKits"),
    name: v.string(),
    images: brandImagesValidator,
    createdByName: v.string(),
    createdAt: v.number(),
    canEdit: v.boolean(),
});
export type BrandKitListing = Infer<typeof brandKitListingValidator>;

/** What putting an image in a slot did: the kit's images after it, or why the kit refused the file. */
export const brandKitImagePutValidator = v.union(
    v.object({ ok: v.literal(true), images: brandImagesValidator }),
    v.object({ ok: v.literal(false), error: v.string() }),
);
export type BrandKitImagePut = Infer<typeof brandKitImagePutValidator>;
